import { authFetch } from '../../../api/csrf';
import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  Award,
  FileText,
  Download,
  Upload,
  Plus,
  Edit,
  Trash2,
  Eye,
  RefreshCw,
  Search,
  User,
  CheckCircle,
  XCircle,
  AlertTriangle,
  AlertCircle,
  Send,
  Copy,
  Star,
  GraduationCap,
  BookOpen,
  Target,
  X,
  Grid,
  List,
  ChevronUp,
  ChevronDown,
  ArrowUpDown,
  ArrowUp,
  ArrowDown,
  Calendar
} from 'lucide-react';
import SelectedExportFormatBar from '../../../components/common/SelectedExportFormatBar';
import { formatDate } from '../../../utils/stringUtils';
import trainingService from '../../../api/services/trainingService';
import certificateService from '../../../api/services/certificateService';
import { usePermissions } from '../../../hooks/usePermissions';
import PermissionGate from '../../../components/common/PermissionGate/PermissionGate';
import { API_BASE_URL, STORAGE_KEYS } from '../../../api/config';
import { post } from '../../../api/apiClient';
import SearchableDropdown from '../../../components/common/SearchableDropdown';
import useDebounce from '../../../hooks/useDebounce';
import Button from '../../../components/common/Button/Button';
import ExportModal from '../../../components/common/ExportModal';
import DateRangeFilter from '../../../components/common/DateRangeFilter';
import {
  exportToCSV,
  generateExportFilename,
  downloadFromBackend
} from '../../../utils/exportUtils';
import toast from 'react-hot-toast';

const CertificatesCredits = () => {
  // Notification helpers using react-hot-toast
  const showSuccess = (message) => toast.success(message);
  const showError = (message) => toast.error(message);
  const showWarning = (message) => toast(message, { duration: 4000 });
  const [searchParams, setSearchParams] = useSearchParams();
  const isInitialFilterMount = useRef(true);
  const isInitialTranscriptFilterMount = useRef(true);
  // Permission hooks
  const { canCreate } = usePermissions();

  // State for certificates
  const [certificates, setCertificates] = useState([]);
  const [showIssueModal, setShowIssueModal] = useState(false);
  const [showViewModal, setShowViewModal] = useState(false);

  // State for transcripts
  const [transcriptSearchTerm, setTranscriptSearchTerm] = useState('');
  const debouncedTranscriptSearchTerm = useDebounce(transcriptSearchTerm, 500);
  const [selectedTranscriptDepartment, setSelectedTranscriptDepartment] = useState('all');
  const [selectedTranscriptStatus, setSelectedTranscriptStatus] = useState('all');
  const [transcriptViewMode, setTranscriptViewMode] = useState('list');
  const [loadingTranscripts, setLoadingTranscripts] = useState(false);
  const [employees, setEmployees] = useState([]);
  const [departments, setDepartments] = useState([]);
  const [selectedCertificate, setSelectedCertificate] = useState(null);
  const [showReplaceModal, setShowReplaceModal] = useState(false);
  const [certificateToReplace, setCertificateToReplace] = useState(null);
  const [showRevokeModal, setShowRevokeModal] = useState(false);
  const [certificateToRevoke, setCertificateToRevoke] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [certificateToDelete, setCertificateToDelete] = useState(null);
  const [isHardDelete, setIsHardDelete] = useState(false);
  const [isBulkHardDelete, setIsBulkHardDelete] = useState(false);
  const [loading, setLoading] = useState(false);
  const [isEmailLoading, setIsEmailLoading] = useState(false);
  const [sendingEmailId, setSendingEmailId] = useState(null);
  const [error, setError] = useState(null);
  const [employeeSearchLoading, setEmployeeSearchLoading] = useState(false);
  const [debouncedEmployeeSearch, setDebouncedEmployeeSearch] = useState('');

  // Bulk upload state
  const [showBulkUploadModal, setShowBulkUploadModal] = useState(false);
  const [bulkFile, setBulkFile] = useState(null);
  const [bulkLoading, setBulkLoading] = useState(false);

  // State for issue certificate form data
  const [availableCourses, setAvailableCourses] = useState([]);
  const [availableQuizzes, setAvailableQuizzes] = useState([]);

  const [transcripts, setTranscripts] = useState([]);
  const [showTranscriptModal, setShowTranscriptModal] = useState(false);

  // State for course search
  const [courses, setCourses] = useState([]);
  const [courseSearchLoading, setCourseSearchLoading] = useState(false);
  const [selectedCourse, setSelectedCourse] = useState(null);

  // State for edit form
  const [editFormData, setEditFormData] = useState({
    category: '',
    description: ''
  });
  const [editFormErrors, setEditFormErrors] = useState({});

  // State for creation form
  const [createFormData, setCreateFormData] = useState({
    category: '',
    description: ''
  });

  // State for credit form errors
  const [createFormErrors, setCreateFormErrors] = useState({});

  // State for certificate form
  const [certificateFormData, setCertificateFormData] = useState({
    employeeName: '',
    courseName: '',
    type: '',
    score: '',
    credits: '',
    expiryDate: ''
  });

  const [employeeAssignments, setEmployeeAssignments] = useState([]);


  // State for certificate form errors
  const [certificateFormErrors, setCertificateFormErrors] = useState({});
  const [certificateTouchedFields, setCertificateTouchedFields] = useState(new Set());
  const expiryDateRef = useRef(null);

  // Handle field blur
  const handleCertificateFieldBlur = (field) => {
    setCertificateTouchedFields(prev => {
      const newSet = new Set(prev);
      newSet.add(field);
      return newSet;
    });
  };

  // State for delete confirmation
  const [deleteConfirmation, setDeleteConfirmation] = useState({
    show: false,
    type: '', // 'credit'
    id: null,
    title: ''
  });

  // State for filters and search — initialized from URL
  const [searchTerm, setSearchTerm] = useState(searchParams.get('search') || '');
  const debouncedSearchTerm = useDebounce(searchTerm, 500);
  const [statusFilter, setStatusFilter] = useState(searchParams.get('status') || 'all');
  const [startDate, setStartDate] = useState(searchParams.get('startDate') || '');
  const [endDate, setEndDate] = useState(searchParams.get('endDate') || '');


  // Export state (ExportModal pattern)
  const [showExportModal, setShowExportModal] = useState(false);
  const [exportLoading, setExportLoading] = useState(false);

  // Row selection (same as SessionsScheduling)
  const [selectedRows, setSelectedRows] = useState(new Map());
  const [showInlineSelectedFormats, setShowInlineSelectedFormats] = useState(false);
  const [inlineFormatPanelEntered, setInlineFormatPanelEntered] = useState(false);
  const selectedExportWrapRef = useRef(null);


  // Bulk delete state
  const [showBulkDeleteConfirm, setShowBulkDeleteConfirm] = useState(false);
  const [bulkDeleteLoading, setBulkDeleteLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(false);

  // Pagination state for certificates — initialized from URL
  const [certificatesCurrentPage, setCertificatesCurrentPage] = useState(Number(searchParams.get('page')) || 1);
  const [certificatesItemsPerPage, setCertificatesItemsPerPage] = useState(Number(searchParams.get('limit')) || 10);
  const [certificatesPagination, setCertificatesPagination] = useState({});

  // Pagination state for transcripts
  const [transcriptsCurrentPage, setTranscriptsCurrentPage] = useState(1);
  const [transcriptsItemsPerPage, setTranscriptsItemsPerPage] = useState(10);
  const [transcriptsPagination, setTranscriptsPagination] = useState({});
  const [sortConfig, setSortConfig] = useState({
    key: searchParams.get('sortBy') || 'issuedDate',
    direction: searchParams.get('sortOrder') || 'desc',
  });
  const [transcriptSortConfig, setTranscriptSortConfig] = useState({ key: 'name', direction: 'asc' });
  const [transcriptModalSortConfig, setTranscriptModalSortConfig] = useState({ key: 'date', direction: 'desc' });

  const baseExportFilters = useMemo(() => {
    const filters = {
      search: debouncedSearchTerm,
      startDate,
      endDate,
    };
    if (statusFilter && statusFilter !== 'all') {
      filters.status = statusFilter;
    }
    return filters;
  }, [debouncedSearchTerm, statusFilter, startDate, endDate]);

  // Sync filter/sort/pagination state to URL search params (persists on refresh)
  useEffect(() => {
    setSearchParams(prev => {
      const params = new URLSearchParams(prev);

      const managedKeys = ['search', 'status', 'startDate', 'endDate', 'page', 'limit', 'sortBy', 'sortOrder'];
      managedKeys.forEach(key => params.delete(key));

      if (debouncedSearchTerm) params.set('search', debouncedSearchTerm);
      if (statusFilter && statusFilter !== 'all') params.set('status', statusFilter);
      if (startDate) params.set('startDate', startDate);
      if (endDate) params.set('endDate', endDate);
      if (certificatesCurrentPage && certificatesCurrentPage !== 1) params.set('page', String(certificatesCurrentPage));
      if (certificatesItemsPerPage && certificatesItemsPerPage !== 10) params.set('limit', String(certificatesItemsPerPage));
      if (sortConfig.key && sortConfig.key !== 'issuedDate') params.set('sortBy', sortConfig.key);
      if (sortConfig.direction && sortConfig.direction !== 'desc') params.set('sortOrder', sortConfig.direction);

      return params;
    }, { replace: true });
  }, [debouncedSearchTerm, statusFilter, startDate, endDate, certificatesCurrentPage, certificatesItemsPerPage, sortConfig, setSearchParams]);

  const requestBackendExport = useCallback(
    async ({
      scope,
      format,
      selectedIds,
      overrideFilters,
      overridePage,
      overrideLimit,
    }) => {
      const filtersToUse = overrideFilters ?? baseExportFilters;
      const exportSortMap = {
        employee: 'metadata.employeeName',
        certificateId: 'certificateNumber',
        certification: 'metadata.courseTitle',
        type: 'contentType',
        issuedDate: 'issuedDate',
        status: 'status',
      };

      const payload = {
        module: "training-certificates",
        scope,
        format,
        filters: filtersToUse,
        page: overridePage ?? certificatesCurrentPage,
        limit: overrideLimit ?? certificatesItemsPerPage,
        sortBy: exportSortMap[sortConfig.key] || sortConfig.key || 'issuedDate',
        sortOrder: sortConfig.direction || 'desc',
        selectedIds: selectedIds ?? [],
      };

      const response = await authFetch(`${API_BASE_URL}/exports/generate`, {
        credentials: 'include',
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          },
        body: JSON.stringify(payload),
      });

      if (!response.ok) {
        const err = await response.json().catch(() => ({}));
        throw new Error(err.responseMessage || err.message || "Export failed");
      }

      const blob = await response.blob();
      const contentDisposition = response.headers.get("Content-Disposition");
      const filenameMatch = contentDisposition?.match(/filename="?(.+?)"?$/);
      const fallbackName = `training-certificates-export.${format === "xlsx" ? "xlsx" : format}`;
      const filename = filenameMatch?.[1]
        ? decodeURIComponent(filenameMatch[1]).replace(/(^"|"$)/g, "")
        : fallbackName;

      const url = window.URL.createObjectURL(blob);
      const link = document.createElement("a");
      link.href = url;
      link.download = filename;
      document.body.appendChild(link);
      link.click();
      document.body.removeChild(link);
      window.URL.revokeObjectURL(url);
    },
    [baseExportFilters, certificatesCurrentPage, certificatesItemsPerPage, sortConfig]
  );


  // Load courses for dropdown
  const loadCourses = async () => {
    try {
      const result = await certificateService.getCoursesForDropdown();
      if (result.success) {
        const sortedCourses = (result.data?.courses || []).sort((a, b) =>
          (a.title || '').localeCompare(b.title || '')
        );
        setCourses(sortedCourses);
      } else {
      }
    } catch (error) {
    }
  };

  // Load active departments for transcripts filter
  const loadDepartments = async () => {
    try {
      const result = await trainingService.getDepartments({ isActive: true, paginated: 'false' });
      if (result.success) {
        const departmentsData = result.data || [];
        setDepartments(departmentsData);
      } else {
        setDepartments([]);
      }
    } catch (error) {
      setDepartments([]);
    }
  };



  // Load employees from training system API with search support
  const loadEmployees = async (search = '') => {
    try {
      // Build query parameters
      const params = new URLSearchParams({
        limit: '1000', // Load more employees for dropdown
        includeAll: 'false'
      });

      if (search && search.trim()) {
        params.append('search', search.trim());
      }

      const response = await authFetch(`${API_BASE_URL}/training-assignments/users?${params.toString()}`, {
        credentials: 'include',
        method: 'GET',
        headers: {
          'Content-Type': 'application/json',
                  }
      });

      const result = await response.json();

      if (result.responseCode === 200) {
        const usersData = result.data?.users || [];

        // Format the data to match frontend expectations
        const formattedEmployees = usersData.map(user => ({
          id: user._id,
          name: `${user.firstName} ${user.lastName}`,
          firstName: user.firstName,
          lastName: user.lastName,
          email: user.email,
          role: user.role,
          department: user.department || 'General',
          totalCertificates: user.certificateStats?.totalCertificates || 0,
          expiredCertificates: user.certificateStats?.expiredCertificates || 0,
          expiringCertificates: user.certificateStats?.expiringCertificates || 0,
          totalCredits: user.certificateStats?.totalCredits || 0
        }));

          setEmployees(formattedEmployees);
      } else {
        setEmployees([]);
      }
    } catch (error) {
      setEmployees([]);
    }
  };

  // Debounce employee search
  useEffect(() => {
    const timer = setTimeout(() => {
      if (debouncedEmployeeSearch && debouncedEmployeeSearch.trim().length >= 2) {
        setEmployeeSearchLoading(true);
        loadEmployees(debouncedEmployeeSearch).finally(() => {
          setEmployeeSearchLoading(false);
        });
      } else if (!debouncedEmployeeSearch || debouncedEmployeeSearch.trim().length === 0) {
        // Load all employees when search is cleared
        loadEmployees();
      }
    }, 300);

    return () => clearTimeout(timer);
  }, [debouncedEmployeeSearch]);

  // Handle employee search with debouncing
  const handleEmployeeSearch = React.useCallback((searchTerm) => {
    setDebouncedEmployeeSearch(searchTerm || '');
  }, []);


  // Transcript functions
  const handleClearTranscriptFilters = () => {
    setTranscriptSearchTerm('');
    setSelectedTranscriptDepartment('all');
    setSelectedTranscriptStatus('all');
  };

  // Certificates functions
  const handleClearCertificateFilters = () => {
    setSearchTerm('');
    setStatusFilter('all');
    setStartDate('');
    setEndDate('');
    setCertificatesCurrentPage(1);
  };

  const getEmployeeStatus = (employee) => {
    const totalCertificates = employee.totalCertificates || 0;
    const expiredCertificates = employee.expiredCertificates || 0;
    const expiringCertificates = employee.expiringCertificates || 0;

    if (totalCertificates === 0) return 'No Certificates';
    if (expiredCertificates > 0) return 'Has Expired';
    if (expiringCertificates > 0) return 'Expiring Soon';
    return 'Active';
  };

  const getEmployeeStatusColor = (employee) => {
    const totalCertificates = employee.totalCertificates || 0;
    const expiredCertificates = employee.expiredCertificates || 0;
    const expiringCertificates = employee.expiringCertificates || 0;

    if (totalCertificates === 0) return 'bg-gray-100 text-gray-800';
    if (expiredCertificates > 0) return 'bg-red-100 text-red-800';
    if (expiringCertificates > 0) return 'bg-yellow-100 text-yellow-800';
    return 'bg-green-100 text-green-800';
  };

  // Filter employees function (moved after state declarations to avoid initialization errors)

  // Load available courses for certificate issue
  const loadAvailableCourses = async () => {
    try {
      const result = await trainingService.getCourses({ status: 'published' });

      if (result.success) {
        // Use the same structure as TrainingCatalog
        const coursesData = result.data?.courses || [];
        setAvailableCourses(Array.isArray(coursesData) ? coursesData : []);
      } else {
        setAvailableCourses([]);
      }
    } catch (error) {
      setAvailableCourses([]);
    }
  };

  // Load available quizzes for certificate issue
  const loadAvailableQuizzes = async () => {
    try {
      const result = await trainingService.getQuizzes();
      if (result.success) {
        const quizzesData = result.data?.quizzes || result.data || [];
        setAvailableQuizzes(Array.isArray(quizzesData) ? quizzesData : []);
      } else {
        setAvailableQuizzes([]);
      }
    } catch (error) {
      setAvailableQuizzes([]);
    }
  };

  // Search courses
  const searchCourses = async (query) => {
    if (query.length < 2) return;

    setCourseSearchLoading(true);
    try {
      const result = await certificateService.searchCourses(query);
      if (result.success) {
        setCourses(result.data?.courses || []);
      } else {
      }
    } catch (error) {
    } finally {
      setCourseSearchLoading(false);
    }
  };

  // Load data when issue modal opens
  useEffect(() => {
    if (showIssueModal) {
      loadEmployees();
      loadAvailableCourses();
      loadAvailableQuizzes();
    } else {
      setEmployeeAssignments([]);
    }
  }, [showIssueModal]);






  // Load certificates from API
  const loadCertificates = async () => {
    setLoading(true);
    setError(null);

    try {
      const queryParams = {
        search: debouncedSearchTerm,
        status: statusFilter !== 'all' ? statusFilter : '',
        sortBy: 'issuedDate',
        sortOrder: 'desc',
        page: certificatesCurrentPage || 1,
        limit: certificatesItemsPerPage || 10
      };

      // Use getComplianceData instead of getCertificates to get pagination
      // The compliance endpoint returns { complianceData: [...], pagination: {...} }
      // while certificates endpoint returns just an array
      const result = await trainingService.getComplianceData(queryParams);

      if (result.success) {

        const certificatesData = result.data?.certificates || result.data?.complianceData || (Array.isArray(result.data) ? result.data : []);
        setCertificates(Array.isArray(certificatesData) ? certificatesData : []);


        const paginationData = result.data?.pagination;

        // Validate pagination object and sync currentPage
        if (paginationData && typeof paginationData === 'object' && paginationData !== null && Object.keys(paginationData).length > 0) {
          setCertificatesPagination(paginationData);
          if (paginationData.currentPage && paginationData.currentPage !== certificatesCurrentPage) {
            setCertificatesCurrentPage(paginationData.currentPage);
          }
        } else {
          const totalItems = certificatesData.length;
          const itemsPerPage = certificatesItemsPerPage || 10;
          const totalPages = Math.ceil(totalItems / itemsPerPage);
          const currentPage = certificatesCurrentPage || 1;
          setCertificatesPagination({
            currentPage: currentPage,
            totalPages: totalPages > 0 ? totalPages : 1,
            totalCertificates: totalItems,
            hasNextPage: currentPage < totalPages,
            hasPrevPage: currentPage > 1,
            limit: itemsPerPage
          });
        }
      } else {
        setError(result.error || 'Failed to load certificates');
      }
    } catch (error) {
      setError('Failed to load certificates');
    } finally {
      setLoading(false);
    }
  };




  // Fetch employee score for selected course/quizz
  const fetchEmployeeScore = async (employeeId, contentId, contentType) => {
    if (!employeeId || !contentId) return;

    try {
      const queryParams = {
        employeeId: employeeId,
        contentType: contentType?.toLowerCase()
      };

      if (contentType === 'Quiz' || contentType === 'Quizz') {
        queryParams.quizId = contentId;
      } else {
        queryParams.courseId = contentId;
      }

      const result = await trainingService.getAssignments(queryParams);

      if (result.success) {
        const assignmentsData = result.data?.assignments || result.data || [];
        if (assignmentsData.length > 0) {
          const assignment = assignmentsData[0];
          const assignee = assignment.assignees?.find(a =>
            (a.userId === employeeId) || (a.user?._id === employeeId) || (a.user === employeeId)
          );

          if (assignee) {
            let score = 0;
            if (contentType === 'Quiz' || contentType === 'Quizz') {
              // Quiz assignment response structure from formatSingleAssignment
              score = assignee.quizAttempt?.score || 0;
            } else {
              // Course assignment response structure from formatSingleAssignment
              // Prefer actual score if available, otherwise fallback to progress percentage
              score = (assignee.score !== undefined && assignee.score !== null)
                ? assignee.score
                : (assignee.progress?.percentage || 0);
            }
            setCertificateFormData(prev => ({ ...prev, score: score.toString() }));
          }
        }
      }
    } catch (error) {
    }
  };

  // Load assignments for selected employee and type
  const loadEmployeeAssignments = async (employeeId, type) => {
    if (!employeeId || !type) {
      setEmployeeAssignments([]);
      return;
    }

    try {
      const queryParams = {
        employeeId: employeeId,
        contentType: type.toLowerCase() === 'quizz' ? 'quiz' : type.toLowerCase(),
        limit: 1000
      };

      const result = await trainingService.getAssignments(queryParams);

      if (result.success) {
        const assignmentsData = result.data?.assignments || result.data || [];
        setEmployeeAssignments(Array.isArray(assignmentsData) ? assignmentsData : []);
      } else {
        setEmployeeAssignments([]);
      }
    } catch (error) {
      setEmployeeAssignments([]);
    }
  };

  // Load employee assignments when employee or type changes
  useEffect(() => {
    if (certificateFormData.employeeName && certificateFormData.type) {
      loadEmployeeAssignments(certificateFormData.employeeName, certificateFormData.type);
      // Reset selected course and score when employee/type changes
      setCertificateFormData(prev => ({
        ...prev,
        courseName: '',
        score: ''
      }));
    } else {
      setEmployeeAssignments([]);
    }
  }, [certificateFormData.employeeName, certificateFormData.type]);

  // Auto-fetch score when employee and course/quizz are selected
  useEffect(() => {
    if (certificateFormData.employeeName && certificateFormData.courseName && certificateFormData.type) {
      fetchEmployeeScore(certificateFormData.employeeName, certificateFormData.courseName, certificateFormData.type);
    }
  }, [certificateFormData.employeeName, certificateFormData.courseName, certificateFormData.type]);




  // Memoized options for course/quiz dropdown
  const certificateContentOptions = useMemo(() => {
    if (!certificateFormData.type) return [];

    const isQuizType = certificateFormData.type?.toLowerCase().startsWith('quiz');

    // If employee is selected, strictly show only their assigned items
    if (certificateFormData.employeeName) {
      return employeeAssignments.map(assignment => {
        const item = isQuizType ? assignment.quiz : assignment.course;
        if (!item) return null;
        return {
          id: item._id || item.id,
          displayText: isQuizType
            ? (item.title || item.name || 'Untitled Quiz')
            : `${item.title}${item.courseCode ? ` (${item.courseCode})` : ''}`,
          title: item.title || item.name,
          courseCode: item.courseCode,
          category: item.category,
          level: item.level,
          searchText: `${item.title || ''} ${item.courseCode || ''}`.toLowerCase()
        };
      }).filter(Boolean);
    }

    // Fallback if no employee is selected: show all available items of that type
    if (isQuizType) {
      return (Array.isArray(availableQuizzes) ? availableQuizzes.map(quiz => ({
        id: quiz.id || quiz._id,
        displayText: quiz.title || quiz.name || 'Untitled Quiz',
        title: quiz.title || quiz.name,
        searchText: `${quiz.title || quiz.name || ''}`.toLowerCase()
      })) : []);
    }

    return (Array.isArray(availableCourses) ? availableCourses.map(course => ({
      id: course.id,
      displayText: `${course.title}${course.courseCode ? ` (${course.courseCode})` : ''}`,
      title: course.title,
      courseCode: course.courseCode,
      category: course.category,
      level: course.level,
      searchText: `${course.title} ${course.courseCode || ''} ${course.category || ''} ${course.level || ''}`.toLowerCase()
    })) : []);
  }, [
    certificateFormData.employeeName,
    certificateFormData.type,
    employeeAssignments,
    availableQuizzes,
    availableCourses
  ]);

  // Filter employees based on search and filters (calculated after all state declarations)
  const filteredEmployees = employees.filter(employee => {
    // Exclude admin users
    const employeeRole = (employee.role || '').toLowerCase();
    const employeeName = (employee.name || '').toLowerCase();
    const employeeEmail = (employee.email || '').toLowerCase();

    if (employeeRole.includes('admin') ||
      employeeName.includes('admin') ||
      employeeEmail.includes('admin')) {
      return false;
    }

    const matchesSearch = !debouncedTranscriptSearchTerm ||
      (employee.name?.toLowerCase().includes(debouncedTranscriptSearchTerm.toLowerCase()) ||
        employee.department?.toLowerCase().includes(debouncedTranscriptSearchTerm.toLowerCase()) ||
        employee.role?.toLowerCase().includes(debouncedTranscriptSearchTerm.toLowerCase()) ||
        employee.email?.toLowerCase().includes(debouncedTranscriptSearchTerm.toLowerCase()));

    const matchesDepartment = selectedTranscriptDepartment === 'all' ||
      employee.department === selectedTranscriptDepartment;

    const matchesStatus = selectedTranscriptStatus === 'all' ||
      (selectedTranscriptStatus.toLowerCase() === 'no certificates'
        ? getEmployeeStatus(employee).toLowerCase() === 'no certificates'
        : getEmployeeStatus(employee).toLowerCase().includes(selectedTranscriptStatus.toLowerCase()));

    return matchesSearch && matchesDepartment && matchesStatus;
  });

  // Client-side pagination for transcripts (calculated after state declarations)
  const sortedEmployees = useMemo(() => {
    const sortableItems = [...filteredEmployees];
    sortableItems.sort((a, b) => {
      let aValue, bValue;

      switch (transcriptSortConfig.key) {
        case 'certificates':
          aValue = a.totalCertificates || 0;
          bValue = b.totalCertificates || 0;
          break;
        case 'status':
          aValue = getEmployeeStatus(a);
          bValue = getEmployeeStatus(b);
          break;
        default:
          aValue = (a[transcriptSortConfig.key] || '').toString().toLowerCase();
          bValue = (b[transcriptSortConfig.key] || '').toString().toLowerCase();
      }

      if (aValue < bValue) return transcriptSortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return transcriptSortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
    return sortableItems;
  }, [filteredEmployees, transcriptSortConfig]);

  const transcriptsTotalPages = Math.ceil(sortedEmployees.length / transcriptsItemsPerPage);
  const transcriptsStartIndex = (transcriptsCurrentPage - 1) * transcriptsItemsPerPage;
  const transcriptsEndIndex = transcriptsStartIndex + transcriptsItemsPerPage;
  const paginatedEmployees = sortedEmployees.slice(transcriptsStartIndex, transcriptsEndIndex);

  const handleTranscriptSort = (key) => {
    let direction = 'asc';
    if (transcriptSortConfig.key === key && transcriptSortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setTranscriptSortConfig({ key, direction });
  };


  const handleTranscriptModalSort = (key) => {
    let direction = 'asc';
    if (transcriptModalSortConfig.key === key && transcriptModalSortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setTranscriptModalSortConfig({ key, direction });
  };

  const sortedModalTranscripts = useMemo(() => {
    if (!Array.isArray(transcripts)) return [];
    const sortableItems = [...transcripts];
    sortableItems.sort((a, b) => {
      let aValue, bValue;
      switch (transcriptModalSortConfig.key) {
        case 'course':
          aValue = (a.certification || a.course?.title || a.courseName || '').toLowerCase();
          bValue = (b.certification || b.course?.title || b.courseName || '').toLowerCase();
          break;
        case 'date':
          aValue = a.issuedDate ? new Date(a.issuedDate) : new Date(0);
          bValue = b.issuedDate ? new Date(b.issuedDate) : new Date(0);
          break;
        case 'score':
          aValue = Number(a.score || 0);
          bValue = Number(b.score || 0);
          break;

        default:
          aValue = (a[transcriptModalSortConfig.key] || '').toString().toLowerCase();
          bValue = (b[transcriptModalSortConfig.key] || '').toString().toLowerCase();
      }
      if (aValue < bValue) return transcriptModalSortConfig.direction === 'asc' ? -1 : 1;
      if (aValue > bValue) return transcriptModalSortConfig.direction === 'asc' ? 1 : -1;
      return 0;
    });
    return sortableItems;
  }, [transcripts, transcriptModalSortConfig]);

  useEffect(() => {
    if (isInitialTranscriptFilterMount.current) {
      isInitialTranscriptFilterMount.current = false;
      return;
    }
    setTranscriptsCurrentPage(1);
  }, [debouncedTranscriptSearchTerm, selectedTranscriptDepartment, selectedTranscriptStatus]);

  // Load employees for transcript dropdowns
  useEffect(() => {
    setLoadingTranscripts(true);
    loadEmployees().finally(() => {
      setLoadingTranscripts(false);
    });
  }, [transcriptsCurrentPage, transcriptsItemsPerPage, debouncedTranscriptSearchTerm, selectedTranscriptDepartment, selectedTranscriptStatus]);

  // Refresh all data
  const refreshData = async () => {
    setLoading(true);
    try {
      await Promise.all([
        loadCertificates()
      ]);
    } catch (error) {
    } finally {
      setLoading(false);
    }
  };

  // Pagination handlers for certificates
  const handleCertificatesPageChange = (page) => {
    setCertificatesCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleCertificatesItemsPerPageChange = (limit) => {
    setCertificatesItemsPerPage(limit);
    setCertificatesCurrentPage(1);
  };

  // Pagination handlers for transcripts (client-side pagination)
  const handleTranscriptsPageChange = (page) => {
    setTranscriptsCurrentPage(page);
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleTranscriptsItemsPerPageChange = (limit) => {
    setTranscriptsItemsPerPage(limit);
    setTranscriptsCurrentPage(1);
  };

  // Reset to page 1 when filters change (skip initial mount to preserve URL page on refresh)
  useEffect(() => {
    if (isInitialFilterMount.current) {
      isInitialFilterMount.current = false;
      return;
    }
    setCertificatesCurrentPage(1);
  }, [debouncedSearchTerm, statusFilter, startDate, endDate]);

  // Debug: Log pagination state changes
  useEffect(() => {
    if (certificatesPagination && Object.keys(certificatesPagination).length > 0) {
    }
  }, [certificatesPagination, loading, certificatesItemsPerPage]);

  // Load certificates on component mount and when filters/pagination change (moved after state declarations)
  // Load departments once on component mount
  useEffect(() => {
    loadDepartments();
  }, []);

  useEffect(() => {
    loadCertificates();
    loadCourses();
  }, [debouncedSearchTerm, statusFilter, certificatesCurrentPage, certificatesItemsPerPage]);


  // Handle certificate viewing
  const handleViewCertificate = (certificate) => {
    setSelectedCertificate(certificate);
    setShowViewModal(true);
  };

  // Handle certificate actions
  const handleIssueCertificate = async (formData) => {
    setIsLoading(true);
    setError(null);

    try {
      const certificateData = {
        employeeId: formData.employeeId,
        courseId: formData.courseId,
        type: formData.type || 'Course',
        score: parseInt(formData.score),
        templateId: null, // Always use hardcoded template
        expiryDate: formData.expiryDate || null
      };

      const result = await certificateService.createCertificate(certificateData);

      if (result.success) {
        // Reload certificates to get the updated list
        await loadCertificates();
        setShowIssueModal(false);
        } else {
        setError(result.error || 'Failed to issue certificate');
      }
    } catch (error) {
      setError('Failed to issue certificate');
    } finally {
      setIsLoading(false);
    }
  };

  // Handle bulk certificate upload
  const handleBulkUpload = async (event) => {
    const file = event.target.files[0];
    if (file) {
      setBulkFile(file);
      setBulkLoading(true);
      setError(null);

      const uploadToastId = toast.loading('Please wait while we process your CSV file...');

      try {
        const result = await certificateService.bulkUploadCertificates(file);

        if (result.success) {
          const data = result.data;
          const createdCount = data.created || 0;
          const errorCount = data.errors || 0;
          const skippedCount = data.skipped || 0;
          const totalRows = data.totalRows || 0;
          const successRate = data.summary?.successRate || 0;

          // Refresh certificates list
          await loadCertificates();

          // Show appropriate toast and close modal
          if (createdCount > 0) {
            toast.success(
              `Successfully created ${createdCount} certificate(s) from ${totalRows} row(s)`,
              { id: uploadToastId }
            );
          } else {
            toast(
              `No certificates were created from ${totalRows} row(s). ${skippedCount} rows were skipped due to missing required fields.`,
              { id: uploadToastId, duration: 5000 }
            );
          }

          // Close modal immediately after showing toast
          setShowBulkUploadModal(false);
          setBulkFile(null);

        } else {
          toast.error(result.error || 'Failed to process file', { id: uploadToastId });
          setError(result.error || 'Failed to process file');

          // Close modal immediately
          setShowBulkUploadModal(false);
          setBulkFile(null);
        }
      } catch (error) {
        toast.error('Failed to process file. Please try again.', { id: uploadToastId });
        setError('Failed to process file');

        // Close modal immediately
        setShowBulkUploadModal(false);
        setBulkFile(null);
      } finally {
        setBulkLoading(false);
      }
    }
  };

  // Handle bulk template download
  const handleDownloadBulkTemplate = async () => {
    try {
      const result = await certificateService.downloadBulkTemplate();
      if (result.success) {
        showSuccess('Template downloaded successfully!');
      } else {
        showError(result.error || 'Failed to download template');
      }
    } catch (error) {
      showError('Failed to download template');
    }
  };


  // Certificate deletion handlers
  const handleConfirmCertificateDelete = async () => {
    if (!certificateToDelete) return;

    setLoading(true);
    try {
      const result = await certificateService.deleteCertificate(certificateToDelete.id, isHardDelete);

      if (result.success) {
        showSuccess(isHardDelete ? 'Certificate permanently deleted' : 'Certificate deactivated');
        await loadCertificates();
        setShowDeleteModal(false);
        setCertificateToDelete(null);
        setIsHardDelete(false);
      } else {
        showError(result.error || 'Failed to delete certificate');
      }
    } catch (error) {
      showError('An error occurred while deleting the certificate');
    } finally {
      setLoading(false);
    }
  };

  const handleCancelCertificateDelete = () => {
    setShowDeleteModal(false);
    setCertificateToDelete(null);
    setIsHardDelete(false);
  };

  const handleReplaceCertificateAction = (certId) => {
    const certificate = certificates.find(cert => cert.id === certId);
    if (!certificate) return;

    // Show confirmation modal
    setCertificateToReplace(certificate);
    setShowReplaceModal(true);
  };

  const handleConfirmReplace = async () => {
    if (!certificateToReplace) return;

    setIsLoading(true);
    setError(null);

    try {
      const result = await certificateService.replaceCertificate(certificateToReplace.id);

      if (result.success) {
        // Reload certificates to get the updated list from database
        await loadCertificates();
        showSuccess('Certificate replaced successfully!');
      } else {
        setError(result.error || 'Failed to replace certificate');
        showError(result.error || 'Failed to replace certificate');
      }
    } catch (error) {
      setError('Failed to replace certificate');
      showError('Failed to replace certificate');
    } finally {
      setIsLoading(false);
      setShowReplaceModal(false);
      setCertificateToReplace(null);
    }
  };

  const handleCancelReplace = () => {
    setShowReplaceModal(false);
    setCertificateToReplace(null);
  };

  const handleRevokeCertificate = (certId) => {
    const certificate = certificates.find(cert => cert.id === certId);
    if (!certificate) return;

    // Show confirmation modal
    setCertificateToRevoke(certificate);
    setShowRevokeModal(true);
  };

  const handleConfirmRevoke = async () => {
    if (!certificateToRevoke) return;

    setIsLoading(true);
    setError(null);

    try {
      const result = await certificateService.revokeCertificate(certificateToRevoke.id);

      if (result.success) {
        // Reload certificates to get the updated list from database
        await loadCertificates();
        showSuccess('Certificate revoked successfully!');
      } else {
        setError(result.error || 'Failed to revoke certificate');
        showError(result.error || 'Failed to revoke certificate');
      }
    } catch (error) {
      setError('Failed to revoke certificate');
      showError('Failed to revoke certificate');
    } finally {
      setIsLoading(false);
      setShowRevokeModal(false);
      setCertificateToRevoke(null);
    }
  };

  const handleCancelRevoke = () => {
    setShowRevokeModal(false);
    setCertificateToRevoke(null);
  };

  const escapeCsvValue = (value) => {
    if (value === null || value === undefined) return '';
    const cell = String(value);
    if (cell.includes('"')) {
      return `"${cell.replace(/"/g, '""')}"`;
    }
    if (cell.includes(',') || cell.includes('\n')) {
      return `"${cell}"`;
    }
    return cell;
  };

  const buildCsvContent = (headers, rows) =>
    [headers, ...rows].map((row) => row.map(escapeCsvValue).join(',')).join('\n');

  const downloadCsvFile = (content, filename) => {
    const blob = new Blob([content], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    window.URL.revokeObjectURL(url);
  };

  const exportCertificatesData = () => {
    if (sortedCertificates.length === 0) {
      showWarning('No certificates available to export.');
      return;
    }

    const rows = sortedCertificates.map((certificate) => {
      const employeeName = certificate.employee?.name || 'Unknown Employee';
      const employeeEmail = certificate.employee?.email || certificate.employee?.emailAddress || '';
      const employeeDepartment =
        certificate.employee?.department || certificate.employee?.departmentName || '';
      const courseTitle = certificate.course?.title || `Unknown ${certificate.type || 'Course'}`;
      const courseCode = certificate.course?.courseId || certificate.course?.courseCode;
      const courseDisplay = courseCode ? `${courseTitle} (${courseCode})` : courseTitle;
      const issuedDate = certificate.issuedDate
        ? new Date(certificate.issuedDate).toLocaleDateString()
        : 'Invalid Date';
      const expiryDate = certificate.expiryDate
        ? new Date(certificate.expiryDate).toLocaleDateString()
        : 'N/A';
      const statusLabel = getCertificateStatusLabel(certificate);

      return [
        employeeName,
        employeeEmail || 'N/A',
        employeeDepartment || 'N/A',
        courseDisplay,
        certificate.type || (certificate.course ? 'Course' : (certificate.metadata?.courseTitle || certificate.certification ? 'Quiz' : 'Course')),
        issuedDate,
        expiryDate,
        statusLabel
      ];
    });

    const csvContent = buildCsvContent(
      ['Employee', 'Email', 'Department', 'Course', 'Type', 'Issue Date', 'Expiry Date', 'Status'],
      rows
    );
    downloadCsvFile(
      csvContent,
      `certificates_export_${new Date().toISOString().split('T')[0]}.csv`
    );
    showSuccess('Certificates exported successfully.');
  };

  const exportTranscriptsData = () => {
    const source = filteredEmployees;

    if (source.length === 0) {
      showWarning('No transcript records available to export.');
      return;
    }

    const rows = source.map((employee) => [
      employee.name || 'Unknown Employee',
      employee.email || 'N/A',
      employee.department || 'N/A',
      employee.role || 'N/A',
      employee.totalCertificates || 0,
      getEmployeeStatus(employee),
      getEmployeeStatus(employee)
    ]);

    const csvContent = buildCsvContent(
      ['Employee', 'Email', 'Department', 'Position', 'Certificates', 'Status'],
      rows
    );
    downloadCsvFile(
      csvContent,
      `transcripts_export_${new Date().toISOString().split('T')[0]}.csv`
    );
    showSuccess('Transcripts exported successfully.');
  };

  // Bulk delete selected certificates
  const handleBulkDelete = async () => {
    if (selectedRows.size === 0) return;
    setBulkDeleteLoading(true);
    const ids = Array.from(selectedRows.keys());
    let successCount = 0;
    let failCount = 0;

    for (const id of ids) {
      try {
        const result = await certificateService.deleteCertificate(id, isBulkHardDelete);
        if (result && result.success) {
          successCount++;
        } else {
          failCount++;
        }
      } catch (err) {
        failCount++;
      }
    }

    setBulkDeleteLoading(false);
    setShowBulkDeleteConfirm(false);
    setSelectedRows(new Map());
    setIsBulkHardDelete(false);

    if (successCount > 0) {
      showSuccess(`${successCount} certificate(s) ${isBulkHardDelete ? 'permanently deleted' : 'deactivated'} successfully${failCount > 0 ? `, ${failCount} failed` : ''}`);
      await loadCertificates();
    } else {
      showError('Failed to delete selected certificates');
    }
  };


  const handleSelectAll = (e) => {
    if (e.target.checked) {
      const newSelected = new Map(selectedRows);
      sortedCertificates.forEach(cert => newSelected.set(cert.id, cert));
      setSelectedRows(newSelected);
    } else {
      setSelectedRows(new Map());
    }
  };

  const handleSelectRow = (cert) => {
    const newSelected = new Map(selectedRows);
    if (newSelected.has(cert.id)) {
      newSelected.delete(cert.id);
    } else {
      newSelected.set(cert.id, cert);
    }
    setSelectedRows(newSelected);
  };

  const mapCertificatesForExport = (certs) =>
    certs.map((certificate) => {
      const employeeName = certificate.employee?.name || 'Unknown Employee';
      const employeeEmail = certificate.employee?.email || certificate.employee?.emailAddress || '';
      const employeeDepartment = certificate.employee?.department || certificate.employee?.departmentName || '';
      const courseTitle = certificate.certification || certificate.course?.title || certificate.metadata?.courseTitle || 'Unknown';
      const courseCode = certificate.course?.courseId || certificate.course?.courseCode || certificate.metadata?.courseCode;
      const courseDisplay = courseCode ? `${courseTitle} (${courseCode})` : courseTitle;
      const issuedDate = certificate.issuedDate ? new Date(certificate.issuedDate).toLocaleDateString() : 'Invalid Date';
      const expiryDate = certificate.expiryDate ? new Date(certificate.expiryDate).toLocaleDateString() : 'N/A';
      const statusLabel = getCertificateStatusLabel(certificate);
      return {
        'Employee': employeeName,
        'Email': employeeEmail || 'N/A',
        'Department': employeeDepartment || 'N/A',
        'Course': courseDisplay,
        'Type': certificate.type || 'Course',
        'Issue Date': issuedDate,
        'Expiry Date': expiryDate,
        'Status': statusLabel
      };
    });

  // Export — ExportModal pattern (same as SessionsScheduling)
  const handleExportClick = () => {
    if (selectedRows.size > 0) {
      setShowInlineSelectedFormats((open) => !open);
      return;
    }
    setShowInlineSelectedFormats(false);
    setShowExportModal(true);
  };

  // Close inline format bar when selection is cleared
  useEffect(() => {
    if (selectedRows.size === 0) setShowInlineSelectedFormats(false);
  }, [selectedRows]);

  // Animate panel in
  useEffect(() => {
    if (!showInlineSelectedFormats) {
      setInlineFormatPanelEntered(false);
      return;
    }
    const id = requestAnimationFrame(() =>
      requestAnimationFrame(() => setInlineFormatPanelEntered(true))
    );
    return () => cancelAnimationFrame(id);
  }, [showInlineSelectedFormats]);

  // Click-outside closes the panel
  useEffect(() => {
    if (!showInlineSelectedFormats) return;
    const onMouseDown = (e) => {
      if (selectedExportWrapRef.current && !selectedExportWrapRef.current.contains(e.target)) {
        setShowInlineSelectedFormats(false);
      }
    };
    document.addEventListener('mousedown', onMouseDown);
    return () => document.removeEventListener('mousedown', onMouseDown);
  }, [showInlineSelectedFormats]);

  const handleExportCurrentPage = async (format) => {
    setExportLoading(true);
    try {
      await requestBackendExport({ scope: 'currentPage', format });
      showSuccess('Current page export successful');
    } catch (err) {
      showError(err.message);
    } finally {
      setExportLoading(false);
      setShowExportModal(false);
    }
  };

  const handleExportFiltered = async (format) => {
    setExportLoading(true);
    try {
      await requestBackendExport({ scope: 'filtered', format });
      showSuccess('Filtered export successful');
    } catch (err) {
      showError(err.message);
    } finally {
      setExportLoading(false);
      setShowExportModal(false);
    }
  };

  const handleExportRange = async (startDate, endDate, format) => {
    setExportLoading(true);
    try {
      await requestBackendExport({
        scope: 'dateRange',
        format,
        overrideFilters: { ...baseExportFilters, startDate, endDate }
      });
      showSuccess('Date range export successful');
    } catch (err) {
      showError(err.message);
    } finally {
      setExportLoading(false);
      setShowExportModal(false);
    }
  };

  const handleExportAll = async (format) => {
    setExportLoading(true);
    try {
      await requestBackendExport({
        scope: 'all',
        format,
        overrideFilters: {},
      });
      showSuccess('All data export successful');
    } catch (err) {
      showError(err.message);
    } finally {
      setExportLoading(false);
      setShowExportModal(false);
    }
  };

  const handleExportSelected = async (format) => {
    setExportLoading(true);
    try {
      const selectedIds = Array.from(selectedRows.keys());
      await requestBackendExport({ scope: 'selected', format, selectedIds });
      showSuccess('Selected rows export successful');
      setSelectedRows(new Map());
      setShowInlineSelectedFormats(false);
    } catch (err) {
      showError(err.message);
    } finally {
      setExportLoading(false);
    }
  };

  const handleSendCertificateEmail = async (certId) => {
    const certificate = (Array.isArray(certificates) ? certificates : []).find(cert => cert.id === certId);
    if (certificate) {
      setSendingEmailId(certId);
      setError(null);
      try {

        const emailData = {
          certificateData: certificate,
          recipientEmail: certificate.employee?.email
        };

        // Call the email API using the API client
        const response = await post('/communications/email', emailData);

        if (response.responseCode === 200) {
          showSuccess('Certificate email sent successfully!');
          setShowViewModal(false); // Close the preview modal
        } else {
          setError('Failed to send certificate email');
          showError('Failed to send certificate email');
        }

      } catch (error) {
        setError('Failed to send certificate email');
        showError('Failed to send certificate email');
      } finally {
        setSendingEmailId(null);
      }
    }
  };

  const handleBulkSendCertificatesEmail = async () => {
    if (selectedRows.size === 0) return;

    setIsEmailLoading(true);

    try {
      const recipients = [];

      // Iterate through the Map values to get certificate objects
      selectedRows.forEach((certificate) => {
        if (certificate && certificate.employee?.email) {
          recipients.push({
            certificateData: certificate,
            recipientEmail: certificate.employee?.email
          });
        }
      });

      if (recipients.length === 0) {
        showWarning('No valid candidates with email addresses selected');
        setIsEmailLoading(false);
        return;
      }

      const bulkData = {
        isBulk: true,
        recipients: recipients
      };

      const response = await post('/communications/email', bulkData);

      if (response.responseCode === 200) {
        showSuccess(`Broadcasting certificates to ${recipients.length} candidates in the background...`);
        setSelectedRows(new Map()); // Reset to a new Map, not a Set
      } else {
        showError('Failed to initiate bulk sending');
      }

    } catch (error) {
      showError('An error occurred while initiating bulk emails');
    } finally {
      setIsEmailLoading(false);
    }
  };



  const handleDownloadCertificate = async (certId) => {
    const certificate = (Array.isArray(certificates) ? certificates : []).find(cert => cert.id === certId) ||
      (Array.isArray(transcripts) ? transcripts.find(cert => cert.id === certId) : null);

    if (!certificate) {
      showError('Certificate data not found');
      return;
    }

    try {
      setLoading(true);
      const now = new Date();
      const datePart = now.toLocaleDateString('en-GB').replace(/\//g, '-');
      const timePart = now.toLocaleTimeString('en-US', {
        hour: '2-digit',
        minute: '2-digit',
        hour12: true
      }).replace(/[: ]/g, '-');

      const fileName = `Certificate_${certificate.certificateNumber || 'Download'}_${datePart}_${timePart}.pdf`;
      const endpoint = `${API_BASE_URL}/certificates/${certId}/download`;

      await downloadFromBackend(endpoint, fileName);
      showSuccess('Certificate downloaded as PDF successfully');
    } catch (error) {
      showError('Failed to download certificate');
    } finally {
      setLoading(false);
    }
  };

  // Calculate if certificate is expired based on expiry date
  const isCertificateExpired = (certificate) => {
    if (!certificate.expiryDate) return false;
    const today = new Date();
    const expiryDate = new Date(certificate.expiryDate);
    return expiryDate < today;
  };

  // Calculate if certificate is expiring soon (within 30 days)
  const isCertificateExpiringSoon = (certificate) => {
    if (!certificate.expiryDate) return false;
    const today = new Date();
    const expiryDate = new Date(certificate.expiryDate);
    const diffTime = expiryDate - today;
    const daysToExpiry = Math.ceil(diffTime / (1000 * 60 * 60 * 24));
    return daysToExpiry > 0 && daysToExpiry <= 30; // Expires within 30 days but not yet expired
  };

  const getStatusColor = (certificate) => {
    // Check if certificate is actually expired based on expiry date
    if (isCertificateExpired(certificate)) {
      return 'text-red-600 bg-red-100';
    }

    // Check if certificate is expiring soon
    if (isCertificateExpiringSoon(certificate)) {
      return 'text-orange-600 bg-orange-100';
    }

    // Check for other statuses
    switch (certificate.status) {
      case 'active': return 'text-green-600 bg-green-100';
      case 'expired': return 'text-red-600 bg-red-100';
      case 'revoked': return 'text-gray-600 bg-gray-100';
      case 'replaced': return 'text-blue-600 bg-blue-100';
      default: return 'text-green-600 bg-green-100'; // Default to active if not expired
    }
  };

  const getStatusIcon = (certificate) => {
    // Check if certificate is actually expired based on expiry date
    if (isCertificateExpired(certificate)) {
      return <XCircle className="h-4 w-4" />;
    }

    // Check if certificate is expiring soon
    if (isCertificateExpiringSoon(certificate)) {
      return <AlertCircle className="h-4 w-4" />;
    }

    // Check for other statuses
    switch (certificate.status) {
      case 'active': return <CheckCircle className="h-4 w-4" />;
      case 'expired': return <XCircle className="h-4 w-4" />;
      case 'revoked': return <AlertTriangle className="h-4 w-4" />;
      case 'replaced': return <RefreshCw className="h-4 w-4" />;
      default: return <CheckCircle className="h-4 w-4" />; // Default to active if not expired
    }
  };

  const normalizedCertificates = useMemo(
    () => (Array.isArray(certificates) ? certificates : []),
    [certificates]
  );

  const filteredCertificates = useMemo(() => {
    const searchLower = (debouncedSearchTerm || '').toLowerCase();

    const checkField = (value) =>
      value && typeof value === 'string' && value.toLowerCase().includes(searchLower);

    return normalizedCertificates.filter((certificate) => {
      const matchesSearch =
        !debouncedSearchTerm ||
        checkField(certificate.employee?.name) ||
        checkField(certificate.employee?.firstName) ||
        checkField(certificate.employee?.lastName) ||
        checkField(certificate.employee?.email) ||
        checkField(certificate.employee?.emailAddress) ||
        checkField(certificate.employee?.userEmail) ||
        checkField(certificate.certification) ||
        checkField(certificate.certificateNumber) ||
        checkField(certificate.course?.title) ||
        checkField(certificate.course?.courseId) ||
        checkField(certificate.course?.courseCode) ||
        (certificate.course &&
          Object.values(certificate.course).some(
            (value) => typeof value === 'string' && checkField(value)
          ));

      let matchesStatus = true;
      if (statusFilter === 'active') {
        matchesStatus =
          !isCertificateExpired(certificate) && !isCertificateExpiringSoon(certificate);
      } else if (statusFilter === 'expiring') {
        matchesStatus = isCertificateExpiringSoon(certificate);
      } else if (statusFilter === 'expired') {
        matchesStatus = isCertificateExpired(certificate);
      } else if (statusFilter === 'revoked') {
        matchesStatus = certificate.status === 'revoked';
      } else if (statusFilter === 'replaced') {
        matchesStatus = certificate.status === 'replaced';
      }

      let matchesDateRange = true;
      if (startDate && endDate) {
        const issuedDate = new Date(certificate.issuedDate);
        const start = new Date(startDate);
        const end = new Date(endDate);
        // Set end to end of day
        end.setHours(23, 59, 59, 999);
        matchesDateRange = issuedDate >= start && issuedDate <= end;
      } else if (startDate) {
        const issuedDate = new Date(certificate.issuedDate);
        const start = new Date(startDate);
        matchesDateRange = issuedDate >= start;
      } else if (endDate) {
        const issuedDate = new Date(certificate.issuedDate);
        const end = new Date(endDate);
        end.setHours(23, 59, 59, 999);
        matchesDateRange = issuedDate <= end;
      }

      return matchesSearch && matchesStatus && matchesDateRange;
    });
  }, [normalizedCertificates, debouncedSearchTerm, statusFilter, startDate, endDate]);

  const handleSort = (key) => {
    let direction = 'asc';
    if (sortConfig.key === key && sortConfig.direction === 'asc') {
      direction = 'desc';
    }
    setSortConfig({ key, direction });
  };

  const sortedCertificates = useMemo(() => {
    const sortableItems = [...filteredCertificates];
    if (sortConfig.key !== null) {
      sortableItems.sort((a, b) => {
        let aValue, bValue;

        switch (sortConfig.key) {
          case 'employee':
            aValue = a.employee?.name || '';
            bValue = b.employee?.name || '';
            break;
          case 'certificateId':
            aValue = a.certificateNumber || '';
            bValue = b.certificateNumber || '';
            break;
          case 'certification':
            aValue = a.certification || a.course?.title || '';
            bValue = b.certification || b.course?.title || '';
            break;
          case 'type':
            aValue = a.type || 'Course';
            bValue = b.type || 'Course';
            break;
          case 'issuedDate':
            aValue = a.issuedDate ? new Date(a.issuedDate) : new Date(0);
            bValue = b.issuedDate ? new Date(b.issuedDate) : new Date(0);
            break;
          case 'status':
            // Custom priority for status: Expired (3) > Expiring (2) > Active (1)
            const getStatusPriority = (cert) => {
              if (isCertificateExpired(cert)) return 3;
              if (isCertificateExpiringSoon(cert)) return 2;
              return 1;
            };
            aValue = getStatusPriority(a);
            bValue = getStatusPriority(b);
            break;
          default:
            aValue = a[sortConfig.key] || '';
            bValue = b[sortConfig.key] || '';
        }

        if (aValue < bValue) {
          return sortConfig.direction === 'asc' ? -1 : 1;
        }
        if (aValue > bValue) {
          return sortConfig.direction === 'asc' ? 1 : -1;
        }
        return 0;
      });
    }
    return sortableItems;
  }, [filteredCertificates, sortConfig]);

  const getCertificateStatusLabel = (certificate) => {
    if (isCertificateExpired(certificate)) return 'Expired';
    if (isCertificateExpiringSoon(certificate)) return 'Expiring';
    return certificate.status || 'Active';
  };

  const shouldShowCertificatesPagination =
    !loading &&
    certificatesPagination &&
    Number(certificatesPagination.totalPages) > 1;

  const lastShownErrorRef = useRef('');

  // Show certificate-related errors in toaster instead of inline header alerts.
  useEffect(() => {
    if (!error) {
      lastShownErrorRef.current = '';
      return;
    }

    if (lastShownErrorRef.current !== error) {
      showError(error);
      lastShownErrorRef.current = error;
    }
  }, [error]);

  return (
    <div className="p-6 bg-gray-50 min-h-screen">
      {/* Header */}
      <div className="mb-8">
        <div className="flex items-center justify-between">
          <div>
            <h1 className="text-3xl font-bold text-gray-900">Certificates & Transcripts</h1>
            <p className="text-gray-600 mt-2">Manage certificates and transcripts</p>
          </div>
          <div className="flex items-center space-x-4">
            {selectedRows.size > 0 && (
              <div className="flex items-center space-x-3 border-r border-gray-300 pr-4 mr-2">
                <PermissionGate permission="training" action="delete">
                  <Button
                    variant="danger"
                    icon={<Trash2 className="h-4 w-4" />}
                    onClick={() => setShowBulkDeleteConfirm(true)}
                    loading={bulkDeleteLoading}
                  >
                    Delete Selected ({selectedRows.size})
                  </Button>
                </PermissionGate>
                <PermissionGate permission="training" action="read">
                  <button
                    onClick={handleBulkSendCertificatesEmail}
                    disabled={isEmailLoading}
                    className="flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors shadow-sm disabled:opacity-50 disabled:cursor-not-allowed"
                  >
                    {isEmailLoading ? (
                      <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Send className="h-4 w-4 mr-2" />
                    )}
                    Bulk Send ({selectedRows.size})
                  </button>
                </PermissionGate>
              </div>
            )}
            <PermissionGate permission="training" action="read">
              <div className="relative shrink-0" ref={selectedExportWrapRef}>
                <Button
                  variant={selectedRows.size > 0 ? 'primary' : 'outline'}
                  icon={<Download className="h-4 w-4" />}
                  onClick={handleExportClick}
                  loading={exportLoading}
                >
                  {selectedRows.size > 0 ? `Export Selected (${selectedRows.size})` : 'Export'}
                </Button>
                <SelectedExportFormatBar
                  show={selectedRows.size > 0 && showInlineSelectedFormats}
                  panelEntered={inlineFormatPanelEntered}
                  disabled={exportLoading}
                  onSelect={handleExportSelected}
                />
              </div>
            </PermissionGate>
            <PermissionGate permission="training" action="create">
              <button
                onClick={() => setShowIssueModal(true)}
                className="flex items-center px-4 py-2 bg-agrosaf-green text-white rounded-lg hover:bg-agrosaf-darkGreen transition-colors"
              >
                <Plus className="h-4 w-4 mr-2" />
                Issue Certificate
              </button>
            </PermissionGate>
          </div>
        </div>
      </div>





      {/* Certificates List */}
      <div className="space-y-6">

        {/* Filters */}
        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4">
          <div className="flex flex-wrap items-end gap-4 w-full">
            <div className="relative flex-[2] min-w-[200px]">
              <Search className="h-4 w-4 absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" />
              <input
                type="text"
                placeholder="Search by Employee, Email, Course or Course ID..."
                value={searchTerm}
                onChange={(e) => {
                  // Allow alphanumeric, spaces, and email special characters (@, ., -, _, +)
                  const value = e.target.value.replace(/[^a-zA-Z0-9\s@._+-]/g, '').slice(0, 100);
                  setSearchTerm(value);
                }}
                onKeyPress={(e) => {
                  // Allow alphanumeric, spaces, and email special characters (@, ., -, _, +)
                  if (!/^[a-zA-Z0-9\s@._+-]$/.test(e.key) && !['Backspace', 'Delete', 'Tab', 'Enter', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
                    e.preventDefault();
                  }
                }}
                maxLength={100}
                className="w-full pl-10 pr-10 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-agrosaf-green focus:border-agrosaf-green"
              />
              {searchTerm && (
                <button
                  onClick={() => {
                    setSearchTerm('');
                    const input = document.querySelector('input[placeholder="Search certificates..."]');
                    if (input) input.focus();
                  }}
                  className="absolute right-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-gray-400 hover:text-gray-600 transition-colors"
                >
                  <X className="h-4 w-4" />
                </button>
              )}
            </div>
            <div className="flex-shrink-0">
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-agrosaf-green focus:border-agrosaf-green"
              >
                <option value="all">All Status</option>
                <option value="active">Active</option>
                <option value="expiring">Expiring</option>
                <option value="expired">Expired</option>
              </select>
            </div>

            <div className="flex-shrink-0">
              <DateRangeFilter
                startDate={startDate}
                endDate={endDate}
                onChange={({ startDate, endDate }) => {
                  setStartDate(startDate);
                  setEndDate(endDate);
                }}
                allowFutureDates={false}
              />
            </div>

            {(searchTerm || statusFilter !== 'all' || startDate || endDate) && (
              <div className="relative flex-shrink-0">
                <Button
                  variant="outline"
                  icon={<X className="h-4 w-4" />}
                  onClick={handleClearCertificateFilters}
                >
                  Clear
                </Button>
              </div>
            )}
          </div>
        </div>

        {/* Certificates Table */}
        <div className="bg-white rounded-lg shadow-sm border">
          <div className="px-6 py-4 border-b border-gray-200">
            <h3 className="text-lg font-semibold text-gray-900">Certificates</h3>
          </div>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200">
              <thead className="bg-gray-50">
                <tr>
                  <th className="px-4 py-3 w-10">
                    <input
                      type="checkbox"
                      className="rounded border-gray-300 text-agrosaf-green focus:ring-agrosaf-green"
                      checked={sortedCertificates.length > 0 && sortedCertificates.every(c => selectedRows.has(c.id))}
                      onChange={handleSelectAll}
                    />
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer group hover:bg-gray-100 transition-colors w-72"
                    onClick={() => handleSort('certificateId')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Certificate ID</span>
                      {sortConfig.key === 'certificateId' ? (
                        sortConfig.direction === 'asc' ? <ArrowUp className="h-3 w-3 flex-shrink-0 text-blue-600" /> : <ArrowDown className="h-3 w-3 flex-shrink-0 text-blue-600" />
                      ) : (
                        <ArrowUpDown className="h-3 w-3 flex-shrink-0 text-gray-400" />
                      )}
                    </div>
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer group hover:bg-gray-100 transition-colors"
                    onClick={() => handleSort('employee')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Employee</span>
                      {sortConfig.key === 'employee' ? (
                        sortConfig.direction === 'asc' ? <ArrowUp className="h-3 w-3 flex-shrink-0 text-blue-600" /> : <ArrowDown className="h-3 w-3 flex-shrink-0 text-blue-600" />
                      ) : (
                        <ArrowUpDown className="h-3 w-3 flex-shrink-0 text-gray-400" />
                      )}
                    </div>
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider w-48 cursor-pointer group hover:bg-gray-100 transition-colors"
                    onClick={() => handleSort('certification')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Course / Quizz</span>
                      {sortConfig.key === 'certification' ? (
                        sortConfig.direction === 'asc' ? <ArrowUp className="h-3 w-3 flex-shrink-0 text-blue-600" /> : <ArrowDown className="h-3 w-3 flex-shrink-0 text-blue-600" />
                      ) : (
                        <ArrowUpDown className="h-3 w-3 flex-shrink-0 text-gray-400" />
                      )}
                    </div>
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer group hover:bg-gray-100 transition-colors"
                    onClick={() => handleSort('type')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Type</span>
                      {sortConfig.key === 'type' ? (
                        sortConfig.direction === 'asc' ? <ArrowUp className="h-3 w-3 flex-shrink-0 text-blue-600" /> : <ArrowDown className="h-3 w-3 flex-shrink-0 text-blue-600" />
                      ) : (
                        <ArrowUpDown className="h-3 w-3 flex-shrink-0 text-gray-400" />
                      )}
                    </div>
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer group hover:bg-gray-100 transition-colors"
                    onClick={() => handleSort('issuedDate')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Created Date</span>
                      {sortConfig.key === 'issuedDate' ? (
                        sortConfig.direction === 'asc' ? <ArrowUp className="h-3 w-3 flex-shrink-0 text-blue-600" /> : <ArrowDown className="h-3 w-3 flex-shrink-0 text-blue-600" />
                      ) : (
                        <ArrowUpDown className="h-3 w-3 flex-shrink-0 text-gray-400" />
                      )}
                    </div>
                  </th>
                  <th
                    className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer group hover:bg-gray-100 transition-colors"
                    onClick={() => handleSort('status')}
                  >
                    <div className="flex items-center space-x-1">
                      <span>Status</span>
                      {sortConfig.key === 'status' ? (
                        sortConfig.direction === 'asc' ? <ArrowUp className="h-3 w-3 flex-shrink-0 text-blue-600" /> : <ArrowDown className="h-3 w-3 flex-shrink-0 text-blue-600" />
                      ) : (
                        <ArrowUpDown className="h-3 w-3 flex-shrink-0 text-gray-400" />
                      )}
                    </div>
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Actions
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan="8" className="px-6 py-12 text-center text-gray-500">
                      <RefreshCw className="h-12 w-12 mx-auto mb-4 text-gray-300 animate-spin" />
                      <p>Loading certificates...</p>
                    </td>
                  </tr>
                ) : sortedCertificates.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="px-6 py-12 text-center text-gray-500">
                      <Award className="h-12 w-12 mx-auto mb-4 text-gray-300" />
                      <p>No certificates found</p>
                      <p className="text-sm">Click "Issue Certificate" to create your first certificate</p>
                    </td>
                  </tr>
                ) : (
                  sortedCertificates.map((certificate) => (
                    <tr key={certificate.id} className={`hover:bg-gray-50 transition-colors ${selectedRows.has(certificate.id) ? 'bg-blue-50/50' : ''}`}>
                      <td className="px-4 py-4">
                        <input
                          type="checkbox"
                          className="rounded border-gray-300 text-agrosaf-green focus:ring-agrosaf-green"
                          checked={selectedRows.has(certificate.id)}
                          onChange={() => handleSelectRow(certificate)}
                        />
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className="text-xs font-mono font-bold text-gray-600 bg-gray-100 px-2 py-1 rounded">
                          {certificate.certificateNumber || 'N/A'}
                        </span>
                      </td>
                      <td className="px-6 py-4">
                        <div className="flex items-center">
                          <div className="w-8 h-8 bg-gray-200 rounded-full flex items-center justify-center flex-shrink-0">
                            <User className="h-4 w-4 text-gray-600" />
                          </div>
                          <div className="ml-3 min-w-0 flex-1">
                            <div className="text-sm font-medium text-gray-900 truncate" title={certificate.employee?.name || 'Unknown Employee'}>
                              {certificate.employee?.name || 'Unknown Employee'}
                            </div>
                            <div className="text-sm text-gray-500 truncate" title={certificate.employee?.email || ''}>
                              {certificate.employee?.email || ''}
                            </div>
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4">
                        <div className="min-w-0 w-40">
                          <div className="text-sm font-medium text-gray-900 truncate" title={certificate.certification || certificate.course?.title || 'Unknown'}>
                            {certificate.certification || certificate.course?.title || 'Unknown'}
                          </div>
                          <div className="text-sm text-gray-500 truncate" title={certificate.course?.courseId || certificate.course?.courseCode || ''}>
                            {certificate.course?.courseId || certificate.course?.courseCode || ''}
                          </div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 capitalize">
                        {certificate.type || 'Course'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        {certificate.issuedDate ? formatDate(certificate.issuedDate, { includeTime: true }) : 'Invalid Date'}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(certificate)}`}>
                          {getStatusIcon(certificate)}
                          <span className="ml-1 capitalize">
                            {isCertificateExpired(certificate) ? 'Expired' :
                              isCertificateExpiringSoon(certificate) ? 'Expiring' :
                                (certificate.status || 'Active')}
                          </span>
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium">
                        <div className="flex items-center space-x-2">
                          <button
                            onClick={() => handleDownloadCertificate(certificate.id)}
                            className="p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-colors"
                            title="Download Certificate"
                          >
                            <Download className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleViewCertificate(certificate)}
                            className="p-2 text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded-lg transition-colors pl-0"
                            title="View Certificate"
                          >
                            <Eye className="h-4 w-4" />
                          </button>
                          <button
                            onClick={() => handleSendCertificateEmail(certificate.id)}
                            className={`p-2 rounded-lg transition-colors pl-0 ${sendingEmailId === certificate.id ? 'text-gray-400 cursor-not-allowed' : 'text-yellow-600 hover:text-yellow-800 hover:bg-yellow-50'}`}
                            title="Send Certificate"
                            disabled={sendingEmailId === certificate.id}
                          >
                            {sendingEmailId === certificate.id ? <RefreshCw className="h-4 w-4 animate-spin" /> : <Send className="h-4 w-4" />}
                          </button>
                          {/* Commented out View Transcript button
                            <button
                              onClick={() => handleViewTranscript(certificate.employee?.id)}
                              className="p-2 text-green-600 hover:text-green-800 hover:bg-green-50 rounded-lg transition-colors"
                              title="View Transcript"
                            >
                              <FileText className="h-4 w-4" />
                            </button>
                            */}
                          <PermissionGate permission="training" action="delete">
                            <button
                              onClick={() => {
                                setCertificateToDelete(certificate);
                                setShowDeleteModal(true);
                                setIsHardDelete(false);
                              }}
                              className="p-2 text-red-600 hover:text-red-800 hover:bg-red-50 rounded-lg transition-colors pl-0"
                              title="Delete Certificate"
                            >
                              <Trash2 className="h-4 w-4" />
                            </button>
                          </PermissionGate>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>

          {/* Certificates Pagination - Show when there are multiple pages */}
          {shouldShowCertificatesPagination && (
            <div className="bg-white rounded-lg border border-gray-200 p-4 mt-6">
              <div className="flex flex-col sm:flex-row items-center justify-between space-y-4 sm:space-y-0">
                {/* Results Count and Limit Selector */}
                <div className="flex items-center space-x-4">
                  <div className="text-sm text-gray-700">
                    Showing {((certificatesPagination.currentPage || certificatesCurrentPage || 1) - 1) * (certificatesPagination.limit || certificatesItemsPerPage || 10) + 1} to {Math.min((certificatesPagination.currentPage || certificatesCurrentPage || 1) * (certificatesPagination.limit || certificatesItemsPerPage || 10), certificatesPagination.totalCompliance || certificatesPagination.totalCertificates || 0)} of {certificatesPagination.totalCompliance || certificatesPagination.totalCertificates || 0} results
                  </div>
                  <div className="flex items-center space-x-2">
                    <label className="text-sm text-gray-700">Items per page:</label>
                    <select
                      value={certificatesItemsPerPage}
                      onChange={(e) => handleCertificatesItemsPerPageChange(Number(e.target.value))}
                      className="px-2 py-1 text-sm border border-gray-300 rounded-lg focus:ring-2 focus:ring-agrosaf-green focus:border-transparent"
                    >
                      <option value={10}>10</option>
                      <option value={25}>25</option>
                      <option value={50}>50</option>
                      <option value={100}>100</option>
                    </select>
                  </div>
                </div>

                {/* Pagination Controls */}
                <div className="flex items-center space-x-2">
                  <button
                    onClick={() => handleCertificatesPageChange((certificatesPagination.currentPage || certificatesCurrentPage || 1) - 1)}
                    disabled={certificatesPagination.hasPrevPage === false || (certificatesPagination.currentPage || certificatesCurrentPage || 1) <= 1}
                    className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Previous
                  </button>

                  {/* Page Numbers */}
                  <div className="flex items-center space-x-1">
                    {(() => {
                      const totalPages = certificatesPagination.totalPages || 1;
                      const currentPageNum = certificatesPagination.currentPage || certificatesCurrentPage || 1;
                      const pages = [];

                      if (totalPages <= 10) {
                        for (let i = 1; i <= totalPages; i++) {
                          pages.push(i);
                        }
                      } else {
                        pages.push(1);
                        if (currentPageNum <= 4) {
                          for (let i = 2; i <= 5; i++) {
                            pages.push(i);
                          }
                          pages.push('ellipsis');
                          pages.push(totalPages);
                        } else if (currentPageNum >= totalPages - 3) {
                          pages.push('ellipsis');
                          for (let i = totalPages - 4; i <= totalPages; i++) {
                            pages.push(i);
                          }
                        } else {
                          pages.push('ellipsis');
                          for (let i = currentPageNum - 1; i <= currentPageNum + 1; i++) {
                            pages.push(i);
                          }
                          pages.push('ellipsis');
                          pages.push(totalPages);
                        }
                      }

                      return pages.map((page, index) => {
                        if (page === 'ellipsis') {
                          return (
                            <span key={`ellipsis-${index}`} className="px-2 text-gray-500">
                              ...
                            </span>
                          );
                        }
                        return (
                          <button
                            key={page}
                            onClick={() => handleCertificatesPageChange(page)}
                            className={`px-3 py-2 text-sm font-medium rounded-md transition-colors ${page === currentPageNum
                              ? 'bg-agrosaf-green text-white'
                              : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                              }`}
                          >
                            {page}
                          </button>
                        );
                      });
                    })()}
                  </div>

                  <button
                    onClick={() => handleCertificatesPageChange((certificatesPagination.currentPage || certificatesCurrentPage || 1) + 1)}
                    disabled={certificatesPagination.hasNextPage === false || (certificatesPagination.currentPage || certificatesCurrentPage || 1) >= (certificatesPagination.totalPages || 1)}
                    className="px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
                  >
                    Next
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Issue Certificate Modal */}
      {showIssueModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 !mt-0">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">Issue Certificate</h3>
                <button
                  onClick={() => {
                    setShowIssueModal(false);
                    setCertificateFormData({ employeeName: '', courseName: '', type: '', score: '', template: '' });
                    setCertificateFormErrors({});
                    setCertificateTouchedFields(new Set());
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XCircle className="h-6 w-6" />
                </button>
              </div>
            </div>
            <div className="p-6">
              <div className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Employee <span className="text-red-500">*</span>
                  </label>
                  <SearchableDropdown
                    options={Array.isArray(employees) ? employees.map(employee => ({
                      id: employee.id,
                      displayText: `${employee.firstName} ${employee.lastName} (${employee.email})`,
                      title: `${employee.firstName} ${employee.lastName}`,
                      searchText: `${employee.firstName} ${employee.lastName} ${employee.email} ${employee.department || ''}`.toLowerCase()
                    })) : []}
                    value={certificateFormData.employeeName || null}
                    onChange={(value) => {
                      setCertificateFormData({ ...certificateFormData, employeeName: value || '' });

                      // Real-time validation for employee selection
                      let error = null;
                      if (!value || (typeof value === 'string' && value.trim() === '')) {
                        error = 'Please select an employee';
                      }

                      setCertificateFormErrors(prev => ({ ...prev, employeeName: error }));
                    }}
                    onSearch={handleEmployeeSearch}
                    placeholder="Select Employee"
                    searchPlaceholder="Search employees by name, email, or department..."
                    error={certificateTouchedFields.has('employeeName') ? certificateFormErrors.employeeName : null}
                    loading={employeeSearchLoading}
                    className="w-full"
                    minSearchLength={2}
                    debounceMs={300}
                    onBlur={() => handleCertificateFieldBlur('employeeName')}
                  />

                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Type <span className="text-red-500">*</span>
                  </label>
                  <select
                    value={certificateFormData.type}
                    onChange={(e) => {
                      const value = e.target.value;
                      setCertificateFormData({ ...certificateFormData, type: value, courseName: '', credits: 0 });

                      let error = null;
                      if (!value) error = 'Please select a type';
                      setCertificateFormErrors(prev => ({ ...prev, type: error, courseName: null }));
                    }}
                    onBlur={() => handleCertificateFieldBlur('type')}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-agrosaf-green focus:border-transparent ${certificateTouchedFields.has('type') && certificateFormErrors.type ? 'border-red-500 bg-red-50' : 'border-gray-300'}`}
                  >
                    <option value="">Select Type</option>
                    <option value="Course">Course</option>
                    <option value="Quiz">Quiz</option>
                  </select>
                  {certificateTouchedFields.has('type') && certificateFormErrors.type && (
                    <p className="mt-1 text-sm text-red-600 flex items-center">
                      <AlertCircle className="h-4 w-4 mr-1" />
                      {certificateFormErrors.type}
                    </p>
                  )}
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    {certificateFormData.type === 'Quiz' ? 'Quiz' : 'Course'} <span className="text-red-500">*</span>
                  </label>
                  <SearchableDropdown
                    options={certificateContentOptions}
                    disabled={!certificateFormData.type}
                    minSearchLength={0}

                    value={certificateFormData.courseName || null}
                    onChange={async (value) => {
                      const selectedCourseId = value;

                      // Real-time validation for course selection
                      let error = null;
                      if (!selectedCourseId || (typeof selectedCourseId === 'string' && selectedCourseId.trim() === '')) {
                        error = `Please select a ${certificateFormData.type === 'Quiz' ? 'quiz' : 'course'}`;
                      }

                      setCertificateFormErrors(prev => ({ ...prev, courseName: error }));

                      setCertificateFormData({
                        ...certificateFormData,
                        courseName: selectedCourseId || '',
                        expiryDate: ''
                      });
                    }}

                    placeholder={`Select ${certificateFormData.type === 'Quiz' ? 'Quiz' : 'Course'}`}
                    searchPlaceholder={`Search ${certificateFormData.type === 'Quiz' ? 'quizzes' : 'courses'} by title...`}
                    error={certificateTouchedFields.has('courseName') ? certificateFormErrors.courseName : null}
                    onBlur={() => handleCertificateFieldBlur('courseName')}
                    className="w-full"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Score <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="number"
                    value={certificateFormData.score}
                    onChange={(e) => {
                      const value = e.target.value;
                      setCertificateFormData({ ...certificateFormData, score: value });

                      // Real-time validation for score
                      let error = null;
                      if (!value || value.toString().trim().length === 0) {
                        error = 'Score is required';
                      } else {
                        const numValue = parseInt(value);
                        if (isNaN(numValue)) error = 'Score must be a number';
                        else if (numValue < 0) error = 'Score cannot be negative';
                        else if (numValue > 100) error = 'Score cannot exceed 100';
                      }
                      setCertificateFormErrors(prev => ({ ...prev, score: error }));
                    }}
                    className={`w-full px-3 py-2 border rounded-lg focus:ring-2 focus:ring-agrosaf-green focus:border-transparent ${certificateTouchedFields.has('score') && certificateFormErrors.score ? 'border-red-500 bg-red-50' : 'border-gray-300'
                      }`}
                    placeholder="Enter score or auto-fill"
                  />

                  {certificateTouchedFields.has('score') && certificateFormErrors.score && (
                    <p className="mt-1 text-sm text-red-600 flex items-center">
                      <AlertCircle className="h-4 w-4 mr-1" />
                      {certificateFormErrors.score}
                    </p>
                  )}
                </div>

                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Expiry Date
                  </label>
                  <div className="relative">
                    <input
                      ref={expiryDateRef}
                      type="date"
                      value={certificateFormData.expiryDate}
                      min={new Date().toISOString().split('T')[0]}
                      onChange={(e) => setCertificateFormData({ ...certificateFormData, expiryDate: e.target.value })}
                      onClick={(e) => e.target.showPicker && e.target.showPicker()}
                      className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-agrosaf-green focus:border-transparent cursor-pointer pr-10"
                    />

                  </div>

                  <p className="mt-1 text-xs text-gray-500">Leave blank if no expiry</p>
                </div>

              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowIssueModal(false);
                  setCertificateFormData({ employeeName: '', courseName: '', type: '', score: '', template: '' });
                  setCertificateFormErrors({});
                  setCertificateTouchedFields(new Set());
                }}
                className="px-4 py-2 text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={() => {
                  const errors = {};
                  if (!certificateFormData.employeeName) errors.employeeName = 'Please select an employee';
                  if (!certificateFormData.courseName) errors.courseName = 'Please select a course';
                  if (!certificateFormData.type) errors.type = 'Please select a type';

                  // Score validation
                  const scoreValue = certificateFormData.score;
                  if (!scoreValue || scoreValue.toString().trim().length === 0) {
                    errors.score = 'Score is required';
                  } else {
                    const numValue = parseInt(scoreValue);
                    if (isNaN(numValue)) {
                      errors.score = 'Score must be a valid number';
                    } else if (numValue < 0) {
                      errors.score = 'Score cannot be negative';
                    } else if (numValue > 100) {
                      errors.score = 'Score cannot exceed 100';
                    }
                  }

                  if (Object.keys(errors).length > 0) {
                    setCertificateFormErrors(errors);
                    setCertificateTouchedFields(new Set(['employeeName', 'courseName', 'type', 'score']));
                    showError('Please fix the highlighted fields');
                    return;
                  }

                  const formData = {
                    employeeId: certificateFormData.employeeName,
                    courseId: certificateFormData.courseName,
                    type: certificateFormData.type,
                    score: parseInt(certificateFormData.score) || 90,
                    templateId: null, // Use hardcoded template


                    expiryDate: certificateFormData.expiryDate || null
                  };
                  handleIssueCertificate(formData);
                }}
                disabled={isLoading}
                className="px-4 py-2 bg-agrosaf-green text-white rounded-lg hover:bg-agrosaf-darkGreen transition-colors disabled:opacity-50 disabled:cursor-not-allowed flex items-center"
              >
                {isLoading ? (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    Issuing...
                  </>
                ) : (
                  <>
                    <Award className="h-4 w-4 mr-2" />
                    Issue Certificate
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Bulk Upload Modal */}
      {showBulkUploadModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 !mt-0">
          <div className="bg-white rounded-lg shadow-xl max-w-2xl w-full mx-4">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">Bulk Upload Certificates</h3>
                <button
                  onClick={() => {
                    setShowBulkUploadModal(false);
                    setBulkFile(null);
                  }}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XCircle className="h-6 w-6" />
                </button>
              </div>
            </div>

            <div className="px-6 py-4">
              <div className="space-y-4">
                {/* File Upload Section */}
                <div className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center">
                  <Upload className="h-12 w-12 mx-auto text-gray-400 mb-4" />
                  <h4 className="text-lg font-medium text-gray-900 mb-2">Upload CSV File</h4>
                  <p className="text-sm text-gray-600 mb-4">
                    Upload a CSV file with certificate data. Download the template for the correct format.
                  </p>

                  <input
                    type="file"
                    accept=".csv"
                    onChange={handleBulkUpload}
                    className="hidden"
                    id="bulk-upload-file"
                  />
                  <label
                    htmlFor="bulk-upload-file"
                    className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors cursor-pointer"
                  >
                    <Upload className="h-4 w-4 mr-2" />
                    Choose CSV File
                  </label>

                  {bulkFile && (
                    <div className="mt-4 p-3 bg-blue-50 border border-blue-200 rounded-lg">
                      <div className="flex items-center">
                        <FileText className="h-5 w-5 text-blue-600 mr-2" />
                        <span className="text-sm font-medium text-blue-900">{bulkFile.name}</span>
                      </div>
                    </div>
                  )}
                </div>

                {/* CSV Format Information */}
                <div className="bg-gray-50 border border-gray-200 rounded-lg p-4">
                  <h5 className="font-medium text-gray-900 mb-2">CSV Format Requirements</h5>
                  <div className="text-sm text-gray-600 space-y-1">
                    <p><strong>Required Fields:</strong></p>
                    <ul className="list-disc list-inside ml-4 space-y-1">
                      <li><code>employeeId</code> - Employee ID from the system</li>
                      <li><code>courseCode</code> - Course Code from the system</li>
                      <li><code>score</code> - Score (0-100)</li>
                    </ul>
                    <p className="mt-2"><strong>Optional Fields:</strong></p>
                    <ul className="list-disc list-inside ml-4 space-y-1">
                      <li><code>templateId</code> - Certificate template ID</li>
                      <li><code>expiryDate</code> - Expiry date (YYYY-MM-DD)</li>

                    </ul>
                  </div>
                </div>

                {/* Download Template Button */}
                <div className="flex justify-center">
                  <button
                    onClick={handleDownloadBulkTemplate}
                    className="flex items-center px-4 py-2 bg-gray-600 text-white rounded-lg hover:bg-gray-700 transition-colors"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Download Template
                  </button>
                </div>

                {/* Loading State */}
                {bulkLoading && (
                  <div className="bg-blue-50 border border-blue-200 rounded-lg p-4">
                    <div className="flex items-center space-x-3">
                      <RefreshCw className="h-5 w-5 text-blue-500 animate-spin" />
                      <div>
                        <p className="text-sm font-medium text-blue-900">Processing CSV file...</p>
                        <p className="text-xs text-blue-600">Please wait while we process your certificates</p>
                      </div>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Certificate View Modal */}
      {showViewModal && selectedCertificate && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 !mt-0">
          <div className="bg-white rounded-lg shadow-xl max-w-5xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">Certificate Details</h3>
                <button
                  onClick={() => setShowViewModal(false)}
                  className="text-gray-400 hover:text-gray-600"
                >
                  <XCircle className="h-6 w-6" />
                </button>
              </div>
            </div>
            <div className="p-6">
              <div className="space-y-6">
                {/* Certificate Information Summary */}
                <div className="bg-gray-50 rounded-xl p-8 border border-gray-200">
                  <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
                    <div className="space-y-4">
                      <div>
                        <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Recipients Information</h4>
                        <p className="text-lg font-bold text-gray-900">{selectedCertificate.employee?.name || 'N/A'}</p>
                        <p className="text-sm text-gray-600">{selectedCertificate.employee?.email || 'N/A'}</p>
                      </div>
                      <div>
                        <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Course / Assessment</h4>
                        <p className="text-lg font-bold text-gray-900">{selectedCertificate.certification || selectedCertificate.course?.title || 'N/A'}</p>
                        <p className="text-sm text-gray-600 capitalize">{selectedCertificate.type || 'Course'}</p>
                      </div>
                    </div>
                    <div className="space-y-4">
                      <div className="grid grid-cols-2 gap-4">
                        <div>
                          <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Issued Date</h4>
                          <p className="text-sm font-semibold text-gray-900">
                            {selectedCertificate.issuedDate ? new Date(selectedCertificate.issuedDate).toLocaleDateString('en-GB', { day: '2-digit', month: 'short', year: 'numeric' }) : '—'}
                          </p>
                        </div>
                        <div>
                          <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Score</h4>
                          <p className="text-sm font-semibold text-gray-900">{selectedCertificate.score || 0}/100</p>
                        </div>
                      </div>
                      <div>
                        <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Certificate ID</h4>
                        <p className="text-sm font-mono text-gray-900 bg-white px-2 py-1 rounded border border-gray-200 inline-block">
                          {selectedCertificate.certificateNumber || 'N/A'}
                        </p>
                      </div>
                      <div>
                        <h4 className="text-xs font-semibold text-gray-500 uppercase tracking-wider mb-1">Status</h4>
                        <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${getStatusColor(selectedCertificate)}`}>
                          {getStatusIcon(selectedCertificate)}
                          <span className="ml-1 capitalize">{selectedCertificate.status || 'Active'}</span>
                        </span>
                      </div>
                    </div>
                  </div>
                </div>

                {/* Certificate Actions */}
                <div className="flex justify-center space-x-4">
                  <button
                    onClick={() => handleDownloadCertificate(selectedCertificate.id)}
                    className="px-6 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors flex items-center"
                  >
                    <Download className="h-4 w-4 mr-2" />
                    Download PDF
                  </button>
                  <button
                    onClick={() => handleSendCertificateEmail(selectedCertificate.id)}
                    disabled={sendingEmailId === selectedCertificate.id}
                    className="px-6 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors flex items-center disabled:opacity-50"
                  >
                    {sendingEmailId === selectedCertificate.id ? (
                      <>
                        <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                        Sending...
                      </>
                    ) : (
                      <>
                        <Send className="h-4 w-4 mr-2" />
                        Email Certificate
                      </>
                    )}
                  </button>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Credit edit/assignment modals temporarily disabled */}
      {/* Transcript Modal */}
      {/* Commented out Transcript Modal
      showTranscriptModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 !mt-0">
          <div className="bg-white rounded-lg shadow-xl max-w-5xl w-full mx-4 max-h-[90vh] overflow-y-auto">
            <div className="px-6 py-4 border-b border-gray-200">
              <div className="flex items-center justify-between">
                <h3 className="text-lg font-semibold text-gray-900">Employee Transcript</h3>
                <button onClick={() => setShowTranscriptModal(false)} className="text-gray-400 hover:text-gray-600">
                  <XCircle className="h-6 w-6" />
                </button>
              </div>
            </div>
            <div className="p-6">
              <div className="space-y-6">
                <div className="grid grid-cols-3 gap-4">
                  <div className="bg-blue-50 p-4 rounded-lg text-center">
                    <BookOpen className="h-8 w-8 text-blue-600 mx-auto mb-2" />
                    <p className="text-2xl font-bold text-blue-600">{Array.isArray(transcripts) ? transcripts.length : 0}</p>
                    <p className="text-sm text-blue-600">Certificates</p>
                  </div>
                </div>
                <div className="border border-gray-200 rounded-lg overflow-hidden">
                  <table className="min-w-full divide-y divide-gray-200">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Course</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Date</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Score</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">Actions</th>
                      </tr>
                    </thead>
                    <tbody className="bg-white divide-y divide-gray-200">
                      {transcripts.length > 0 ? transcripts.map((cert) => (
                        <tr key={cert.id} className="hover:bg-gray-50">
                          <td className="px-4 py-3 text-sm">{cert.certification || cert.courseName}</td>
                          <td className="px-4 py-3 text-sm">{cert.issuedDate}</td>
                          <td className="px-4 py-3 text-sm">{cert.score}</td>
                          <td className="px-4 py-3">
                            <button onClick={() => handleDownloadCertificate(cert.id)} className="text-blue-600"><Download className="h-4 w-4" /></button>
                          </td>
                        </tr>
                      )) : <tr><td colSpan="4" className="text-center py-4">No certificates found</td></tr>}
                    </tbody>
                  </table>
                </div>
              </div>
            </div>
            <div className="px-6 py-4 border-t border-gray-200 flex justify-end">
              <button onClick={() => setShowTranscriptModal(false)} className="px-4 py-2 bg-gray-100 rounded-lg">Close</button>
            </div>
          </div>
        </div>
      )
      */}

      {/* Delete Confirmation Modal */}
      {deleteConfirmation.show && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 !mt-0">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center mb-4">
              <AlertCircle className="h-6 w-6 text-red-600 mr-3" />
              <h3 className="text-lg font-semibold text-gray-900">
                Confirm Deletion
              </h3>
            </div>
            <p className="text-gray-600 mb-6">
              Are you sure you want to delete the {deleteConfirmation.type} for <strong>"{deleteConfirmation.title}"</strong>? This action cannot be undone.
            </p>
            <div className="flex items-center justify-end space-x-3">
              <button
                onClick={handleCancelDelete}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmDelete}
                disabled={loading}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 disabled:cursor-not-allowed"
              >
                {loading ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Replace Certificate Confirmation Modal */}
      {showReplaceModal && certificateToReplace && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 !mt-0">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center mb-4">
              <AlertCircle className="h-6 w-6 text-orange-600 mr-3" />
              <h3 className="text-lg font-semibold text-gray-900">
                Replace Certificate
              </h3>
            </div>
            <div className="mb-4">
              <p className="text-gray-600 mb-3">
                Are you sure you want to replace this certificate?
              </p>
              <div className="bg-gray-50 p-3 rounded-lg">
                <p className="text-sm text-gray-700">
                  <strong>Employee:</strong> {certificateToReplace.employee?.name || 'Unknown'}
                </p>
                <p className="text-sm text-gray-700">
                  <strong>Course:</strong> {certificateToReplace.certification || certificateToReplace.course?.title || 'Unknown'}
                </p>
                <p className="text-sm text-gray-700">
                  <strong>Certificate #:</strong> {certificateToReplace.certificateNumber || certificateToReplace.certificateId}
                </p>
              </div>
              <p className="text-sm text-gray-500 mt-3">
                This will mark the current certificate as 'replaced' and you can issue a new one.
              </p>
            </div>
            <div className="flex items-center justify-end space-x-3">
              <button
                onClick={handleCancelReplace}
                disabled={loading}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmReplace}
                disabled={loading}
                className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 transition-colors disabled:opacity-50 flex items-center"
              >
                {loading ? (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    Replacing...
                  </>
                ) : (
                  'Replace Certificate'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Revoke Certificate Confirmation Modal */}
      {showRevokeModal && certificateToRevoke && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 !mt-0">
          <div className="bg-white rounded-lg p-6 max-w-md w-full mx-4">
            <div className="flex items-center mb-4">
              <AlertCircle className="h-6 w-6 text-red-600 mr-3" />
              <h3 className="text-lg font-semibold text-gray-900">
                Revoke Certificate
              </h3>
            </div>
            <div className="mb-4">
              <p className="text-gray-600 mb-3">
                Are you sure you want to revoke this certificate?
              </p>
              <div className="bg-gray-50 p-3 rounded-lg">
                <p className="text-sm text-gray-700">
                  <strong>Employee:</strong> {certificateToRevoke.employee?.name || 'Unknown'}
                </p>
                <p className="text-sm text-gray-700">
                  <strong>Course:</strong> {certificateToRevoke.certification || certificateToRevoke.course?.title || 'Unknown'}
                </p>
                <p className="text-sm text-gray-700">
                  <strong>Certificate #:</strong> {certificateToRevoke.certificateNumber || certificateToRevoke.certificateId}
                </p>
              </div>
              <p className="text-sm text-red-600 mt-3 font-medium">
                ⚠️ This action cannot be undone.
              </p>
            </div>
            <div className="flex items-center justify-end space-x-3">
              <button
                onClick={handleCancelRevoke}
                disabled={loading}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmRevoke}
                disabled={loading}
                className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 transition-colors disabled:opacity-50 flex items-center"
              >
                {loading ? (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    Revoking...
                  </>
                ) : (
                  'Revoke Certificate'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Delete Certificate Confirmation Modal */}
      {showDeleteModal && certificateToDelete && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70] !mt-0 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-md w-full overflow-hidden">
            <div className={`p-4 flex items-center border-b ${isHardDelete ? 'bg-red-50 border-red-100' : 'bg-orange-50 border-orange-100'}`}>
              <div className={`p-2 rounded-lg mr-3 ${isHardDelete ? 'bg-red-100' : 'bg-orange-100'}`}>
                {isHardDelete ? <Trash2 className="h-6 w-6 text-red-600" /> : <AlertTriangle className="h-6 w-6 text-orange-600" />}
              </div>
              <h3 className="text-lg font-bold text-gray-900">
                {isHardDelete ? 'Hard Delete Certificate' : 'Delete Certificate'}
              </h3>
            </div>

            <div className="p-6">
              <p className="text-gray-600 mb-4">
                {isHardDelete
                  ? 'Warning: This action will permanently remove the certificate from the database. This cannot be undone.'
                  : 'Are you sure you want to delete this certificate? This will deactivate the certificate record.'}
              </p>

              <div className="bg-gray-50 p-4 rounded-xl border border-gray-100 mb-6 font-medium">
                <div className="grid grid-cols-2 gap-2 text-sm">
                  <span className="text-gray-500">Employee:</span>
                  <span className="text-gray-900">{certificateToDelete.employee?.name || 'Unknown'}</span>
                  <span className="text-gray-500">Course / Quiz:</span>
                  <span className="text-gray-900">{certificateToDelete.certification || certificateToDelete.course?.title || 'Unknown'}</span>
                  <span className="text-gray-500">Certificate #:</span>
                  <span className="text-gray-900 font-mono text-xs">{certificateToDelete.certificateNumber || certificateToDelete.certificateId}</span>
                </div>
              </div>

              {!isHardDelete && (
                <div className="flex items-center mb-6">
                  <input
                    type="checkbox"
                    id="hardDeleteCheck"
                    className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded cursor-pointer"
                    checked={isHardDelete}
                    onChange={(e) => setIsHardDelete(e.target.checked)}
                  />
                  <label htmlFor="hardDeleteCheck" className="ml-2 block text-sm text-red-600 font-medium cursor-pointer">
                    Perform permanent (hard) delete
                  </label>
                </div>
              )}

              {isHardDelete && (
                <div className="flex items-center mb-6 p-3 bg-red-50 rounded-lg border border-red-200">
                  <input
                    type="checkbox"
                    id="hardDeleteConfirm"
                    className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded cursor-pointer"
                    checked={isHardDelete}
                    onChange={(e) => {
                      if (!e.target.checked) setIsHardDelete(false);
                    }}
                  />
                  <label htmlFor="hardDeleteConfirm" className="ml-2 block text-xs text-red-800 font-bold cursor-pointer">
                    I UNDERSTAND THIS IS PERMANENT AND NON-REVERSIBLE
                  </label>
                </div>
              )}
            </div>

            <div className="px-6 py-4 bg-gray-50 flex items-center justify-end space-x-3">
              <button
                onClick={handleCancelCertificateDelete}
                disabled={isLoading}
                className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors"
              >
                Cancel
              </button>
              <button
                onClick={handleConfirmCertificateDelete}
                disabled={isLoading}
                className={`px-4 py-2 text-sm font-medium text-white rounded-lg transition-colors flex items-center ${isHardDelete ? 'bg-red-600 hover:bg-red-700 shadow-lg shadow-red-100' : 'bg-orange-600 hover:bg-orange-700'}`}
              >
                {isLoading ? (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    Deleting...
                  </>
                ) : (
                  isHardDelete ? 'Permanently Delete' : 'Deactivate Certificate'
                )}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Export Modal */}
      <ExportModal
        isOpen={showExportModal}
        onClose={() => setShowExportModal(false)}
        loading={exportLoading}
        title="Export Certificates"
        scopedFormatExport={{
          onExport: (scope, format, meta) => {
            if (scope === 'currentPage') return handleExportCurrentPage(format);
            if (scope === 'filtered') return handleExportFiltered(format);
            if (scope === 'dateRange') return handleExportRange(meta.startDate, meta.endDate, format);
            if (scope === 'all') return handleExportAll(format);
          }
        }}
      />

      {/* Bulk Delete Confirmation */}
      {showBulkDeleteConfirm && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[70] !mt-0">
          <div className="bg-white rounded-xl p-6 w-full max-w-md shadow-2xl m-4">
            <div className="flex items-center mb-4">
              <div className={`p-2 rounded-lg mr-3 ${isBulkHardDelete ? 'bg-red-100' : 'bg-orange-100'}`}>
                <Trash2 className={`h-6 w-6 ${isBulkHardDelete ? 'text-red-600' : 'text-orange-600'}`} />
              </div>
              <h3 className="text-lg font-bold text-gray-900">
                {isBulkHardDelete ? 'Hard Delete Selected' : 'Delete Selected Certificates'}
              </h3>
            </div>
            <p className="text-gray-600 mb-2">
              You are about to {isBulkHardDelete ? 'permanently delete' : 'deactivate'} <span className={`font-semibold ${isBulkHardDelete ? 'text-red-600' : 'text-orange-600'}`}>{selectedRows.size} certificate(s)</span>.
            </p>
            <p className="text-sm text-gray-500 mb-6">
              {isBulkHardDelete
                ? '⚠️ WARNING: Action is permanent and cannot be undone. All selected records will be wiped.'
                : 'Deactivated certificates will no longer be visible in active lists but remain in the database.'}
            </p>

            <div className="flex items-center mb-6 p-3 bg-gray-50 rounded-lg border border-gray-200">
              <input
                type="checkbox"
                id="bulkHardDeleteCheck"
                className="h-4 w-4 text-red-600 focus:ring-red-500 border-gray-300 rounded cursor-pointer"
                checked={isBulkHardDelete}
                onChange={(e) => setIsBulkHardDelete(e.target.checked)}
              />
              <label htmlFor="bulkHardDeleteCheck" className="ml-2 block text-sm text-red-700 font-bold cursor-pointer">
                Perform permanent (hard) delete
              </label>
            </div>

            <div className="flex justify-end space-x-3">
              <button
                onClick={() => {
                  setShowBulkDeleteConfirm(false);
                  setIsBulkHardDelete(false);
                }}
                disabled={bulkDeleteLoading}
                className="px-4 py-2 text-gray-700 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 transition-colors disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={handleBulkDelete}
                disabled={bulkDeleteLoading}
                className={`px-4 py-2 text-white rounded-lg transition-colors disabled:opacity-50 flex items-center ${isBulkHardDelete ? 'bg-red-600 hover:bg-red-700' : 'bg-agrosaf-green hover:bg-agrosaf-darkGreen'}`}
              >
                {bulkDeleteLoading ? (
                  <>
                    <RefreshCw className="h-4 w-4 mr-2 animate-spin" />
                    Processing...
                  </>
                ) : (
                  <>
                    <Trash2 className="h-4 w-4 mr-2" />
                    {isBulkHardDelete ? 'Permanently Delete' : 'Delete'} {selectedRows.size} Certificate(s)
                  </>
                )}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default CertificatesCredits;
